/**
 * CancelJobModal
 *
 * Confirmation modal for cancelling a Running or Queued AgentJob.
 * Requires a free-text reason (min 10 chars) before the Cancel Job button
 * is enabled — guarding against accidental destructive action.
 *
 * US-24-GR-06 — Cancel Running or Queued AgentJob with Auto-Refund
 */

import { useEffect, useRef, useState } from 'react';

export interface CancelJobModalProps {
  isOpen: boolean;
  jobId: string;
  currentStatus: string;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

const MIN_REASON_LENGTH = 10;

/** Minimal spinner — animation gated on motion-safe (prefers-reduced-motion) */
function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin"
    />
  );
}

export function CancelJobModal({
  isOpen,
  jobId,
  currentStatus,
  onConfirm,
  onCancel,
}: CancelJobModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const keepRunningButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const [reason, setReason] = useState('');
  const [isInFlight, setIsInFlight] = useState(false);

  // Focus the "Keep Running" (safe) button when modal opens; restore focus on close.
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = setTimeout(() => {
        keepRunningButtonRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    } else {
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen]);

  // ESC to cancel + focus trap
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isInFlight) onCancel();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInFlight, onCancel]);

  if (!isOpen) return null;

  async function handleConfirm() {
    if (isInFlight || reason.length < MIN_REASON_LENGTH) return;
    setIsInFlight(true);
    try {
      await onConfirm(reason);
    } finally {
      setIsInFlight(false);
    }
  }

  const isSubmitEnabled = reason.length >= MIN_REASON_LENGTH && !isInFlight;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        aria-hidden="true"
        onClick={() => { if (!isInFlight) onCancel(); }}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-job-title"
          aria-describedby="cancel-job-desc"
          className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          {/* Title */}
          <h2
            id="cancel-job-title"
            className="text-sm font-semibold text-zinc-100"
          >
            Cancel job?
          </h2>

          <div id="cancel-job-desc" className="flex flex-col gap-3">
            {/* Job meta */}
            <div className="flex gap-2 flex-wrap">
              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400 font-mono truncate max-w-[280px]">
                {jobId}
              </span>
              <span className="text-xs bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded text-amber-300">
                {currentStatus}
              </span>
            </div>

            {/* Warning callout */}
            <div className="rounded-lg bg-red-950/40 border border-red-500/30 px-3 py-2">
              <p className="text-xs text-red-300">
                This will fail the job, refund credits, and notify the user.
              </p>
            </div>

            {/* Reason textarea */}
            <div>
              <label
                htmlFor="cancel-job-reason"
                className="block text-xs font-medium text-zinc-400 mb-1"
              >
                Cancellation reason
              </label>
              <textarea
                id="cancel-job-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isInFlight}
                placeholder="Why are you cancelling this job? (min 10 characters)"
                rows={4}
                className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-red-500/70 disabled:opacity-50 resize-none"
                aria-describedby="cancel-job-char-count"
                aria-required="true"
              />
              <p
                id="cancel-job-char-count"
                className={`mt-1 text-xs text-right ${
                  reason.length >= MIN_REASON_LENGTH
                    ? 'text-emerald-400'
                    : 'text-zinc-500'
                }`}
              >
                {reason.length} / {MIN_REASON_LENGTH} min
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            {/* Keep Running — default focus, safe action */}
            <button
              ref={keepRunningButtonRef}
              type="button"
              onClick={onCancel}
              disabled={isInFlight}
              aria-label="Keep running — do not cancel this job"
              className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40 transition-colors"
            >
              Keep Running
            </button>

            {/* Cancel Job — destructive, requires reason */}
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={!isSubmitEnabled}
              aria-label={`Cancel job ${jobId}`}
              aria-disabled={isSubmitEnabled ? 'false' : 'true'}
              className="min-h-[48px] px-5 py-2 rounded-lg bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-2 transition-colors"
            >
              {isInFlight ? (
                <>
                  <Spinner />
                  Cancelling…
                </>
              ) : (
                'Cancel Job'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
